/**
 * stepViewModel — everything the six views need, derived ONCE from a status
 * snapshot.
 *
 * Story 26739. Both hosts render the same panes, so both hosts must read the
 * same fields out of the same snapshot. Doing that derivation per host is how
 * ora and the add-in ended up disagreeing about what a run's steps were; doing
 * it per BODY, inside the add-in, is how completed runs ended up on a
 * different surface from live ones. So it happens here, once, and the hosts
 * pass the result down.
 *
 * EVERY READ IS DEFENSIVE AND EVERY MISS IS EXPLICIT. A value this module
 * cannot find comes back `null`, and the views render "—" plus "not on the
 * policy today", a disabled hop with a reason, or nothing at all. None of them
 * renders a guess. The snapshot is caller-shaped JSON from the substrate: the
 * keys below are the ones observed on `carrier_mortgagee_update_v2`, and a
 * workflow that spells one differently must show a gap, not an invention.
 *
 * Pure. No React, no Office.js, no Remix server import (design Constraint 20).
 */

import type { ConfidenceLevel } from "../components/confidenceChip";
import type { OldNewRow } from "./OldNewDiff";
import type { PacketRow } from "./CarrierPacketView";
import { warningsFromStatus } from "../resultWarnings";
import {
  acknowledgedSummary,
  readWarningItems,
  type AcknowledgedSummary,
} from "../warningAcknowledgement";

/** The loose snapshot shape both hosts hold. */
export type StatusLike = Record<string, unknown> | null | undefined;

function obj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

function str(v: unknown): string | null {
  if (typeof v === "string" && v.trim() !== "") return v.trim();
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return null;
}

/**
 * Are these two the same value for diff purposes?
 *
 * Case- and whitespace-insensitive on purpose: "WELLS FARGO BANK NA" from an
 * extraction and "Wells Fargo Bank NA" from Epic are the same mortgagee, and
 * striking the first through to replace it with the second would show the
 * reader a change that is only a difference in transcription. `null` on either
 * side is never "the same" — an unknown old value is a gap, not a match.
 */
function sameValue(a: string | null, b: string | null): boolean {
  if (a == null || b == null) return false;
  return a.trim().replace(/\s+/g, " ").toLowerCase() ===
    b.trim().replace(/\s+/g, " ").toLowerCase();
}

/** First non-empty string found at any of `keys` across all `sources`. */
function pick(
  sources: (Record<string, unknown> | null)[],
  keys: readonly string[],
): string | null {
  for (const source of sources) {
    if (!source) continue;
    for (const key of keys) {
      const v = str(source[key]);
      if (v) return v;
    }
  }
  return null;
}

/** Candidate keys for the saved reply draft, most specific first. */
const DRAFT_ID_KEYS = [
  "reply_draft_immutable_id",
  "draft_message_id",
  "reply_draft_id",
  "draft_id",
] as const;

/**
 * Candidate keys for the Epic endorsement / activity the run produced.
 *
 * Searched in `metadata` only. These were also looked for in a `status.outputs`
 * object, along with DRAFT_ID_KEYS and PORTAL_URL_KEYS — but no code path has
 * ever emitted that key, so those reads were always empty and are gone. If the
 * endorsement number never appears on the Done pane, the reason is that
 * nothing puts it in `metadata`; do NOT re-add an `outputs` source to
 * "fix" it.
 */
const ENDORSEMENT_KEYS = [
  "pchg_activity_id",
  "pchg_activity_uuid",
  "endorsement_number",
  "activity_id",
] as const;

/** Candidate keys for a carrier portal URL. */
const PORTAL_URL_KEYS = ["portal_url", "carrier_portal_url", "url"] as const;

/**
 * The labels of the fields ora resolves for itself before it ever asks —
 * Level 2 lookups, not extractions, which is why none of them carries a
 * confidence chip (`confidenceChip.ts` owns that split).
 */
const RESOLVED_FIELD_LABELS: readonly {
  label: string;
  keys: readonly string[];
}[] = [
  { label: "Policy number", keys: ["policy_number"] },
  { label: "Policy type", keys: ["policy_type_code", "policy_type"] },
  { label: "Term dates", keys: ["term_effective_date", "term_expiration_date"] },
  { label: "Client", keys: ["client_name", "client_sk"] },
  { label: "Branch", keys: ["branch", "branch_code"] },
];

/**
 * Can the done view claim success? (Ruling R35 — fail safe.)
 *
 * - `succeeded`: status is exactly `completed`, not ended early, no warnings.
 * - `succeeded_with_warnings`: the same, but warnings are flagged.
 * - `not_confirmed`: everything else, including a completed run whose outcome
 *   fields are ABSENT (`ended_early` not an explicit false, or `result_warnings`
 *   not an array — R38c: absence of evidence is not evidence). Also failed, cancelled, timeout_cancelled,
 *   rejected, completed-but-ended-early, and an unknown or missing status. Never
 *   defaults to success: a warning that did not need to fire costs a glance, a
 *   success claim for a failed run costs a client.
 */
export type StepOutcome =
  | "succeeded"
  | "succeeded_with_warnings"
  | "not_confirmed";

export const NOT_CONFIRMED_SENTENCE =
  "This run did not finish — the endorsement may not be in Epic. " +
  "Check Epic and the run record before telling the client it's done.";
export const WITH_WARNINGS_SENTENCE =
  "This run finished with warnings — check the run record before telling the client it's done.";

export const REPLY_WARNING_SENTENCE =
  "This run reported warnings — check Epic and the run record before you send the reply.";

export interface StepViewModel {
  clientName: string | null;
  policyNumber: string | null;
  /** Block A of the brief — field NAMES only. */
  resolvedFields: string[];
  /** "Change type · Replace mortgagee", or null when the run does not say. */
  changeLead: string | null;
  /** Block B of the brief. */
  diffRows: OldNewRow[];
  /** The carrier packet's copy-ready rows. */
  packetRows: PacketRow[];
  /** Only when a real http(s) URL exists — a dead link is worse than none. */
  portalUrl: string | null;
  /** Reply-ready's proof line. Null unless the run positively reported clean (no early exit, no warnings). */
  proofSentence: string | null;
  /** Reply-ready's three lines. Empty unless the run positively reported clean. */
  bullets: string[];
  /** Reply-ready only: the warning shown INSTEAD of proof/bullets when the parked run is not clean. Null otherwise. */
  replyWarning: string | null;
  /** Done's one sentence. */
  doneSentence: string | null;
  /**
   * True when the run reached a terminal state WITHOUT doing the work — the
   * safe-rerun guard found the change already completed in Epic, a gate was
   * rejected, a gate timed out. Read from the status payload's `ended_early`.
   *
   * Load-bearing, not decoration: every "what ora did" line in this model is
   * otherwise unconditional, so an early-exit run rendered the full success
   * story — "the endorsement is recorded in Epic", "<name> is now the
   * mortgagee of record" — for work that never happened (local instance
   * 37917ab1, a duplicate-detected run, 2026-09-10).
   */
  endedEarly: boolean;
  /**
   * Whether the Done view may claim success. Use this, not `endedEarly`, for
   * "can we say it worked?" — `endedEarly` is only one of the ways to be
   * `not_confirmed`.
   */
  outcome: StepOutcome;
  /**
   * Warning acknowledgement (2026-10-06): who acknowledged the run's warning
   * lines, when EVERY line is acknowledged (server-confirmed); null otherwise.
   * It adds a calm line under the done sentence and changes nothing else: the
   * outcome, the eyebrow and the sentence still say what happened — a run that
   * ended early or did not complete is never re-worded as a success.
   */
  acknowledged: AcknowledgedSummary | null;
  /** The saved reply draft, when the run recorded one. */
  draftMessageId: string | null;
  /** The email the run was launched from. */
  sourceMessageId: string | null;
  /** The old mortgagee, when the run named which interest it is replacing. */
  targetInterest: string | null;
}

export interface BuildStepViewModelInput {
  status?: StatusLike;
  hitlQuestion?: unknown;
  /** Per-field extraction confidence — chips only where it belongs. */
  fieldConfidence?: Record<string, ConfidenceLevel>;
  /** Falls back for a snapshot whose `inputs.client_name` was cleared. */
  clientNameFallback?: string | null;
}

export function buildStepViewModel({
  status,
  hitlQuestion,
  fieldConfidence,
  clientNameFallback,
}: BuildStepViewModelInput): StepViewModel {
  const snapshot = obj(status);
  const inputs = obj(snapshot?.inputs) ?? {};
  const metadata = obj(snapshot?.metadata) ?? {};
  const clientCtx = obj(metadata.client_context);
  const ctxClient = obj(clientCtx?.client);
  const ctxPolicy = obj(clientCtx?.policy);
  const question = obj(hitlQuestion);
  const draft = obj(question?.mortgagee_draft);
  const mortgagees = Array.isArray(draft?.mortgagees) ? draft.mortgagees : [];
  const newMortgagee = obj(mortgagees[0]) ?? {};

  const clientName =
    pick([inputs], ["client_name"]) ??
    pick([ctxClient], ["name"]) ??
    (typeof clientNameFallback === "string" && clientNameFallback.trim() !== ""
      ? clientNameFallback.trim()
      : null);
  const policyNumber =
    pick([inputs], ["policy_number"]) ?? pick([ctxPolicy], ["number"]);

  const resolvedFields = RESOLVED_FIELD_LABELS.filter(({ keys }) =>
    keys.some((k) => str(inputs[k]) != null || str(ctxPolicy?.[k]) != null),
  ).map(({ label }) => label);

  const changeType = pick([inputs, question], ["change_type"]);
  const changeLead = changeType ? `Change type · ${changeType}` : null;

  const targetInterest = pick(
    [question, inputs],
    ["target_interest", "old_mortgagee_name"],
  );
  const effectiveDate = pick([inputs, question], ["effective_date"]);

  const newName = str(newMortgagee.name);
  const newLoan = str(newMortgagee.loan_number);
  const newAddress = str(newMortgagee.mailing_address);

  const diffRows: OldNewRow[] = [];
  if (newName) {
    diffRows.push({
      label: "Mortgagee",
      before: targetInterest,
      after: newName,
      // The ONE row with both sides, so the ONE row that can claim a change
      // that did not happen: an AM editing only the address or the loan number
      // leaves the name identical on both sides, and the row then read "was
      // <name> → now <name>" with the old value struck through. The row STAYS
      // (it is the identity of what is being changed, and `ExceptionView`
      // reads its `after`); what goes is the false claim.
      changed: !sameValue(targetInterest, newName),
      confidence: fieldConfidence?.["mortgagees.name"],
    });
  }
  if (newLoan) {
    diffRows.push({
      label: "Loan #",
      // The substrate publishes the NEW loan number and nothing about the old
      // one; showing a blank here would read as "unchanged".
      before: null,
      after: newLoan,
      confidence: fieldConfidence?.["mortgagees.loan_number"],
    });
  }
  if (newAddress) {
    diffRows.push({
      label: "Mailing addr",
      before: null,
      after: newAddress,
      confidence: fieldConfidence?.["mortgagees.mailing_address"],
    });
  }
  if (effectiveDate) {
    diffRows.push({
      label: "Effective",
      before: null,
      after: effectiveDate,
      confidence: fieldConfidence?.effective_date,
    });
  }

  const packetRows: PacketRow[] = [];
  if (policyNumber) packetRows.push({ label: "Policy #", value: policyNumber });
  if (newName) packetRows.push({ label: "Mortgagee", value: newName });
  if (newLoan) packetRows.push({ label: "Loan #", value: newLoan });
  if (newAddress) packetRows.push({ label: "Mailing", value: newAddress });
  if (effectiveDate) packetRows.push({ label: "Effective", value: effectiveDate });

  const rawPortal = pick([question, metadata], PORTAL_URL_KEYS);
  const portalUrl =
    rawPortal && /^https?:\/\//i.test(rawPortal) ? rawPortal : null;

  // The run reached a terminal state without doing the work. Published flat on
  // the status payload beside `result_warnings` (substrate `status.py`), so it
  // arrives with the snapshot both hosts already pass in — no second fetch and
  // no host wiring. Checked BEFORE every claim below.
  const endedEarly = snapshot?.ended_early === true;
  const earlyExitReason = str(snapshot?.early_exit_reason);

  // Warning acknowledgement (2026-10-06): the published lines, null on an API
  // that predates them. Every line acknowledged → who and when, for Done.
  const warningLines = readWarningItems(snapshot);
  const acknowledged = acknowledgedSummary(warningLines);

  // Positive evidence only. The per-instance status carries `result_warnings`;
  // `has_warnings` is the LIST projection of the same predicate and is read
  // defensively in case a caller hands one through.
  const hasWarnings =
    snapshot?.has_warnings === true ||
    warningsFromStatus(snapshot).length > 0 ||
    // R57/R59/R66: ANY published line is a warning, so a run the pill marks
    // "Completed*" never reads as a clean "Done" — including a merged snapshot
    // whose lines (a union) include one its outcome fields (one snapshot) lack.
    // No lines (an older API) → exactly as before.
    (warningLines ?? []).length > 0 ||
    // Lines present but unreadable (a malformed sibling): still lines.
    (Array.isArray(snapshot?.warning_items) && snapshot.warning_items.length > 0);
  // "Clean" needs BOTH outcome fields present and benign: `ended_early` an
  // explicit false and `result_warnings` an array. An absent field is absence
  // of evidence (a live push snapshot before the authoritative fetch, an older
  // substrate) — it is never read as "nothing went wrong" (R38c).
  const evidenceClean =
    snapshot?.ended_early === false &&
    Array.isArray(snapshot?.result_warnings) &&
    !hasWarnings;
  const outcome: StepOutcome =
    snapshot?.status !== "completed" || endedEarly
      ? "not_confirmed"
      : hasWarnings
        ? "succeeded_with_warnings"
        : evidenceClean
          ? "succeeded"
          : "not_confirmed";

  // Reply-ready (`pending_email_send`) is a live, parked state AFTER the
  // endorsement step and is not part of the done-view outcome, but its claims
  // ("Done in Epic.", "<name> is now the mortgagee of record.") are just as
  // false when the run reported warnings or ended early (R37) — the v2
  // orchestration attaches finish warnings to runs that park here. Same
  // positive-evidence test as the done view.
  const parked = snapshot?.status === "pending_email_send";
  const claimsOk = parked ? evidenceClean && !endedEarly : outcome === "succeeded";
  const replyWarning = parked && !claimsOk ? REPLY_WARNING_SENTENCE : null;

  const endorsement = pick([metadata], ENDORSEMENT_KEYS);
  // "Done in Epic." is a claim; it needs positive evidence.
  const proofSentence = !claimsOk
    ? null
    : endorsement
      ? `Done in Epic. Endorsement ${endorsement} recorded.`
      : "Done in Epic.";

  // Every bullet asserts something ora did. An empty list renders nothing,
  // which is the honest output — the reason lives in `doneSentence`.
  const bullets: string[] = [];
  if (claimsOk) {
    if (newName) bullets.push(`${newName} is now the mortgagee of record.`);
    if (targetInterest) bullets.push(`${targetInterest} was removed.`);
    bullets.push("The reply is drafted, not sent.");
  }

  // When success is not confirmed the SERVER's own reason is the sentence (one
  // copy of the wording, accurate for every early-exit case), else the fixed
  // warning. Deliberately NOT a client-side summary: a late-gate rejection CAN
  // happen after the endorsement exists. Never depends on the substrate
  // supplying a reason (older substrates, missing results).
  const doneSentence =
    outcome === "not_confirmed"
      ? (earlyExitReason ?? NOT_CONFIRMED_SENTENCE)
      : outcome === "succeeded_with_warnings"
        ? WITH_WARNINGS_SENTENCE
        : endorsement
          ? `Endorsement ${endorsement} is recorded in Epic for the Servicer.`
          : "This run is finished — the endorsement is recorded in Epic for the Servicer.";

  return {
    clientName,
    policyNumber,
    resolvedFields,
    changeLead,
    diffRows,
    packetRows,
    portalUrl,
    proofSentence,
    bullets,
    replyWarning,
    doneSentence,
    endedEarly,
    outcome,
    acknowledged,
    draftMessageId: pick([metadata, inputs], DRAFT_ID_KEYS),
    sourceMessageId: pick([metadata], ["graph_message_id"]),
    targetInterest,
  };
}

export default buildStepViewModel;
